import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authApi, ApiError } from '../api/client';
import { loginSchema } from '../schemas/validation';
import { cn } from '../utils/cn';
import { normalizeEmail } from '../utils/email';
import { resolvePostAuthPath } from '../utils/postAuthNavigation';
import { getSafeRedirectPath } from '../utils/safeRedirect';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = getSafeRedirectPath(searchParams.get('redirect'));
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setIsLoading(true);

    try {
      // Validate form data
      const validatedData = loginSchema.parse(formData);

      // Call API (canonical email; server also normalizes)
      const response = await authApi.login({
        ...validatedData,
        email: normalizeEmail(validatedData.email),
      });

      // Store user info (you might want to use context or state management)
      localStorage.setItem('user', JSON.stringify(response.user));

      const u = response.user as { emailVerified?: boolean; isAdmin?: boolean };
      if (!u.isAdmin && u.emailVerified === false) {
        if (redirectParam) {
          try {
            sessionStorage.setItem('afterEmailVerify', redirectParam);
          } catch {
            /* ignore */
          }
        }
        navigate('/verify-email', { replace: true });
        return;
      }
      if (redirectParam) {
        navigate(redirectParam, { replace: true });
        return;
      }
      const destination = await resolvePostAuthPath(u);
      navigate(destination, { replace: true });
    } catch (error) {
      if (error instanceof ApiError) {
        setErrors({ api: error.message });
      } else if (error instanceof Error) {
        // Zod validation errors
        setErrors({ api: error.message });
      } else {
        setErrors({ api: 'An unexpected error occurred' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to Website Builder
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link
              to={
                redirectParam ? `/signup?redirect=${encodeURIComponent(redirectParam)}` : '/signup'
              }
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              create a new account
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                required
                value={formData.email}
                onChange={handleChange}
                className={cn(
                  'input-field mt-1',
                  errors.email && 'border-red-300 focus:border-red-500 focus:ring-red-500'
                )}
                placeholder="Enter your email"
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleChange}
                className={cn(
                  'input-field mt-1',
                  errors.password && 'border-red-300 focus:border-red-500 focus:ring-red-500'
                )}
                placeholder="Enter your password"
              />
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
            </div>
          </div>

          {errors.api && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-600">{errors.api}</p>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className={cn('btn-primary w-full', isLoading && 'opacity-50 cursor-not-allowed')}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
